// -----------------------------------------------------------------------------
// src/components/dashboard/CampaignDetailsPanel.js
// -----------------------------------------------------------------------------
import React from "react";
import {
  Typography,
  List,
  ListItem,
  Box,
  Chip,
  ListItemAvatar,
  Avatar,
  Grid,
  Button,
  Card,
  CardMedia,
  CardContent,
  Link,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PeopleIcon from "@mui/icons-material/People";
import SendIcon from "@mui/icons-material/Send";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import TikTokIcon from "@mui/icons-material/MusicNote";
import InstagramIcon from "@mui/icons-material/CameraAlt";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PersonIcon from "@mui/icons-material/Person";
import profilePhoto from "../../../../../Components/globalAssets/ppfLogo.png";
import {
  getFollowersByVideos,
  getTotalFollowersInDict,
} from "../../../../../Utils/constants";

// ────────── Status utils ──────────
export const CreatorCampaignStatus = [
  "Pitched",
  "Approved",
  "Counter",
  "Dropped",
  "Completed",
];

const STATUS_COLORS = {
  Approved: "#c8e6c9",
  Counter: "#ffecb3",
  Dropped: "#ffcdd2",
  Completed: "#bbdefb",
};

const formatNumber = (num) => Number(num || 0).toLocaleString();

const getStatusOutline = (status) => ({
  border: `2px solid ${STATUS_COLORS[status] || "transparent"}`,
  backgroundColor: "transparent",
});

// ────────── NEW: Helper to check if pricing should be shown ──────────
const shouldShowPrice = (campaignDetails) => {
  return !campaignDetails?.hide_public_pricing;
};

// ────────── Styled wrappers ──────────
const CreatorCard = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  marginBottom: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  padding: theme.spacing(2),
  transition: "transform 0.2s, box-shadow 0.2s",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: theme.shadows[4],
  },
}));

const DetailGrid = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1),
  marginTop: theme.spacing(0.5),
  flexWrap: "wrap",
}));

const StyledCard = styled(Card)(({ theme }) => ({
  ...theme.typography.body2,
  height: "100%",
  boxShadow: theme.shadows[2],
}));

// ────────── Platform Icon Helper ──────────
const getPlatformIcon = (platform) => {
  switch (platform?.toLowerCase()) {
    case "tiktok":
      return <TikTokIcon fontSize="small" />;
    case "instagram":
      return <InstagramIcon fontSize="small" />;
    case "youtube":
      return <YouTubeIcon fontSize="small" />;
    default:
      return <PersonIcon fontSize="small" />;
  }
};

// ────────── Platform Name Helper ──────────
const getPlatformName = (platform) => {
  return platform || "Unknown Platform";
};

/* ════════════ Re-usable tooltip - UPDATED ════════════ */
const PriceTooltip = ({ creator, children, hidePricing }) => {
  // Don't wrap in tooltip if pricing is hidden
  if (hidePricing) {
    return children;
  }

  return (
    <Tooltip
      arrow
      placement="top"
      title={
        <Box sx={{ px: 0.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Preferred rate
          </Typography>
          {creator.platformLink && (
            <Link
              href={creator.platformLink}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
            >
              Open profile
            </Link>
          )}
        </Box>
      }
    >
      {children}
    </Tooltip>
  );
};

/* ════════════ Grid-view card - UPDATED ════════════ */
const CreatorProfileCard = ({
  creator,
  onStatusChange,
  editable,
  onNegotiateClick,
  hidePricing = false, // NEW: Add this prop
}) => {
  const handleStatusButtonClick = (newStatus) => {
    onStatusChange(creator.id, newStatus);
  };

  return (
    <StyledCard sx={{ ...getStatusOutline(creator.status), position: "relative" }}>
      <CardMedia
        component="img"
        height="180"
        image={creator.pfphref || profilePhoto}
        alt={creator.name}
        sx={{ objectFit: "cover" }}
      />

      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {/* Name / profile link with platform icon */}
        <Typography variant="h6" fontWeight={600}>
          <Link
            href={creator.platformLink || "#"}
            target="_blank"
            rel="noopener noreferrer"
            color="text.primary"
            underline="hover"
            sx={{ display: "flex", alignItems: "center", gap: 1 }}
          >
            {getPlatformIcon(creator.promotionPlatform)}
            @{creator.name}
          </Link>
        </Typography>

        {/* Followers + platform link */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <PeopleIcon fontSize="small" />
          <Typography variant="body2" color="text.secondary">
            Followers:&nbsp;
            {(getTotalFollowersInDict(getFollowersByVideos(creator.videos)) || 0).toLocaleString()}
          </Typography>
          {creator.platformLink && (
            <Link
              href={creator.platformLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ mt: 1 }}
            >
              View on {getPlatformName(creator.promotionPlatform)}
            </Link>
          )}
        </Box>

        {/* Status chips */}
        <DetailGrid>
          <Chip
            icon={<SendIcon fontSize="small" />}
            label={creator.offerSent ? "Offer Sent" : "Pending Offer"}
            size="small"
            variant="outlined"
          />
          <Chip
            icon={<AssignmentIcon fontSize="small" />}
            label={creator.assetsSent ? "Assets Sent" : "Assets Pending"}
            size="small"
            variant="outlined"
          />
        </DetailGrid>

        {/* Counter notice */}
        {creator.status === "Counter" && (
          <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
            <ErrorOutlineIcon sx={{ color: "error.main", mr: 0.5 }} />
            <Typography variant="body2" color="error.main">
              This is the creator's desired rate
            </Typography>
          </Box>
        )}

        {/* Floating price badge with tooltip - CONDITIONAL */}
        {!hidePricing && (
          <PriceTooltip creator={creator} hidePricing={hidePricing}>
            <Box
              sx={{
                position: "absolute",
                top: 8,
                left: 8,
                backgroundColor: "primary.main",
                color: "primary.contrastText",
                px: 1.5,
                py: 0.5,
                borderRadius: 1,
                fontWeight: 700,
                fontSize: { xs: "0.8rem", sm: "1rem" },
                cursor: hidePricing ? "default" : "help",
              }}
            >
              ${formatNumber(creator.price)}
            </Box>
          </PriceTooltip>
        )}

        {/* Action buttons */}
        {editable && (
          <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
            <Button
              variant="contained"
              color="error"
              fullWidth
              onClick={() => handleStatusButtonClick("Dropped")}
            >
              Drop
            </Button>
            <Button
              variant="contained"
              color="success"
              fullWidth
              onClick={() => handleStatusButtonClick("Approved")}
            >
              Approve
            </Button>
            <Button
              variant="contained"
              color="warning"
              fullWidth
              onClick={() => onNegotiateClick(creator)}
            >
              Negotiate
            </Button>
          </Box>
        )}
      </CardContent>
    </StyledCard>
  );
};

/* ════════════ Main panel (list & grid) - UPDATED ════════════ */
const CampaignDetailsPanel = ({
  campaignDetails,
  isCampaignActive,
  handleStatusChange,
  onNegotiateClick,
  viewMode = "list",
}) => {
  // NEW: Check if pricing should be hidden
  const hidePricing = !shouldShowPrice(campaignDetails);

  /* ----- Desktop-first "list" mode ----- */
  const renderListMode = () => (
    <List dense sx={{ py: 0 }}>
      {campaignDetails.creators.map((creator, index) => (
        <CreatorCard key={creator.id || index} sx={getStatusOutline(creator.status)}>
          <ListItem
            disablePadding
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              flexWrap: { xs: "wrap", md: "nowrap" },
            }}
            data-creator-name={creator.name.toLowerCase()}
          >
            {/* Avatar */}
            <ListItemAvatar>
              <Avatar
                src={creator.pfphref || profilePhoto}
                sx={{
                  width: 56,
                  height: 56,
                  boxShadow: 3,
                  border: "2px solid",
                  borderColor: "background.paper",
                }}
              />
            </ListItemAvatar>

            {/* Creator info */}
            <Box sx={{ flexGrow: 1, ml: 2, display: "flex", flexDirection: "column" }}>
              <Typography variant="h6" fontWeight={600}>
                <Link
                  href={creator.platformLink || "#"}
                  target="_blank"
                  rel="noopener noreferrer"
                  color="text.primary"
                  underline="hover"
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  {getPlatformIcon(creator.promotionPlatform)}
                  @{creator.name}
                </Link>
              </Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}>
                <PeopleIcon fontSize="small" sx={{ color: "text.secondary" }} />
                <Typography variant="body2" color="text.secondary">
                  Followers:&nbsp;
                  {(getTotalFollowersInDict(getFollowersByVideos(creator.videos)) || 0).toLocaleString()}
                </Typography>
              </Box>

              {creator.platformLink && (
                <Link
                  href={creator.platformLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                >
                  View on {getPlatformName(creator.promotionPlatform)}
                </Link>
              )}
            </Box>

            {/* Actions / chips */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "flex-end",
                gap: 1,
                minWidth: { xs: "100%", md: 225 },
                mt: { xs: 2, md: 0 },
              }}
            >
              {/* BIG price (desktop) wrapped in tooltip - CONDITIONAL */}
              {!hidePricing && (
                <PriceTooltip creator={creator} hidePricing={hidePricing}>
                  <Box
                    sx={{
                      minWidth: 120,
                      display: { xs: "none", md: "flex" },
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: hidePricing ? "default" : "help",
                    }}
                  >
                    <Typography
                      variant="h4"
                      fontWeight={800}
                      color="primary.main"
                      sx={{ lineHeight: 1 }}
                    >
                      ${formatNumber(creator.price)}
                    </Typography>
                  </Box>
                </PriceTooltip>
              )}

              {/* Mobile fallback price chip - CONDITIONAL */}
              <DetailGrid>
                {!hidePricing && (
                  <Chip
                    label={`$${formatNumber(creator.price)}`}
                    color="primary"
                    size="small"
                    variant="outlined"
                    sx={{ display: { xs: "inline-flex", md: "none" } }}
                    component={(props) => (
                      <PriceTooltip creator={creator} hidePricing={hidePricing}>
                        <span {...props} style={{ cursor: hidePricing ? "default" : "help" }} />
                      </PriceTooltip>
                    )}
                  />
                )}
                <Chip
                  icon={<SendIcon fontSize="small" />}
                  label={creator.offerSent ? "Offer Sent" : "Pending Offer"}
                  size="small"
                  variant="outlined"
                />
                <Chip
                  icon={<AssignmentIcon fontSize="small" />}
                  label={creator.assetsSent ? "Assets Sent" : "Assets Pending"}
                  size="small"
                  variant="outlined"
                />
              </DetailGrid>

              {/* Counter notice */}
              {creator.status === "Counter" && (
                <Box sx={{ display: "flex", alignItems: "center", mt: 1 }}>
                  <ErrorOutlineIcon sx={{ color: "error.main", mr: 0.5 }} />
                  <Typography variant="body2" color="error.main">
                    This is the creator's desired rate
                  </Typography>
                </Box>
              )}

              {/* Action buttons */}
              {isCampaignActive && (
                <Box sx={{ display: "flex", gap: 1.5, mt: 1 }}>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleStatusChange(creator.id, "Dropped")}
                  >
                    Drop
                  </Button>
                  <Button
                    variant="contained"
                    color="success"
                    onClick={() => handleStatusChange(creator.id, "Approved")}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={() => onNegotiateClick(creator)}
                  >
                    Negotiate
                  </Button>
                </Box>
              )}
            </Box>
          </ListItem>
        </CreatorCard>
      ))}
    </List>
  );

  /* ----- "Dashboard" grid mode - UPDATED ----- */
  const renderGridMode = () => (
    <Grid container spacing={2}>
      {campaignDetails.creators.map((creator) => (
        <Grid item xs={12} sm={6} md={4} key={creator.id}>
          <CreatorProfileCard
            creator={creator}
            onStatusChange={handleStatusChange}
            editable={isCampaignActive}
            onNegotiateClick={onNegotiateClick}
            hidePricing={hidePricing}
          />
        </Grid>
      ))}
    </Grid>
  );

  return viewMode === "list" ? renderListMode() : renderGridMode();
};

export default CampaignDetailsPanel;